import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Layer, Marker, Source, useMap } from 'react-map-gl';
import { PlannerAction, PlannerState, RouteRiskReport } from '../types';
import { Position, insertionIndexForPoint, midpointOfLine, nearestPointOnLine } from '../geo/insertionIndex';
import { formatDelta, formatDistance, formatDuration } from '../geo/format';
import { buildCongestionGradient } from './congestion';
import { buildRiskGradient, riskColor } from './risk';

/** Every id the planner puts on the map, so teardown can be exhaustive. */
export const NS = 'route-planner';
const SRC_ALTERNATIVES = `${NS}/alternatives`;
const SRC_PRIMARY = `${NS}/primary`;
const SRC_FAILED = `${NS}/failed`;
const LYR_ALT_CASING = `${NS}/alt-casing`;
const LYR_ALT_LINE = `${NS}/alt-line`;
const LYR_PRIMARY_CASING = `${NS}/primary-casing`;
const LYR_PRIMARY_LINE = `${NS}/primary-line`;
const LYR_FAILED = `${NS}/failed-line`;

const GOLD = '#f5b942';
const PRIMARY_BLUE = '#4c8bf5';
const MUTED = '#8fa3bf';
/** Matches the ALT marker in the Directions tab. */
const ALTERNATE_TEAL = '#00b8a9';
const GHOST_HIT_RADIUS_PX = 22;

interface RoutePlannerLayersProps {
  state: PlannerState;
  legOffsets: number[][];
  dispatch: React.Dispatch<PlannerAction>;
  /** Suppress interactions while another tool owns the map. */
  interactive?: boolean;
  /** Synced marker driven by the elevation chart. */
  hoverPoint?: { longitude: number; latitude: number } | null;
  /** Corridor analysis; when present it recolours the primary line. */
  risk?: RouteRiskReport | null;
}

const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const RoutePlannerLayers: React.FC<RoutePlannerLayersProps> = ({
  state,
  legOffsets,
  dispatch,
  interactive = true,
  hoverPoint,
  risk,
}) => {
  const { current: mapRef } = useMap();
  const [ghost, setGhost] = useState<{ position: Position; index: number } | null>(null);
  const [draggingGhost, setDraggingGhost] = useState(false);
  const [tooltip, setTooltip] = useState<{ position: Position; index: number } | null>(null);
  const draggingRef = useRef(false);
  draggingRef.current = draggingGhost;

  const primary = state.routes[state.primaryIndex] || null;
  const primaryCoords = useMemo(
    () => ((primary?.geometry?.coordinates || []) as Position[]),
    [primary],
  );

  const primaryData = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!primary) return emptyCollection;
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: primary.geometry }],
    };
  }, [primary]);

  const alternativesData = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: state.routes
      .map((route, index) => ({ route, index }))
      .filter(({ index }) => index !== state.primaryIndex)
      .map(({ route, index }) => ({
        type: 'Feature' as const,
        id: index,
        properties: {
          routeIndex: index,
          hovered: state.hoverIndex === index,
          // The kept fallback is drawn as a road we intend to be able to take,
          // not as one of the options the router happened to return.
          alternate: index === state.alternateIndex,
        },
        geometry: route.geometry,
      })),
  }), [state.routes, state.primaryIndex, state.alternateIndex, state.hoverIndex]);

  const failedData = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: state.failedLegs
      .map((leg) => {
        const from = state.waypoints[leg.fromIndex];
        const to = state.waypoints[leg.toIndex];
        if (!from || !to) return null;
        return {
          type: 'Feature' as const,
          properties: { message: leg.message },
          geometry: {
            type: 'LineString' as const,
            coordinates: [[from.longitude, from.latitude], [to.longitude, to.latitude]],
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[],
  }), [state.failedLegs, state.waypoints]);

  const congestionGradient = useMemo(
    () => buildCongestionGradient(primaryCoords, primary?.congestion),
    [primaryCoords, primary],
  );

  // Corridor risk answers the question the operator asked for, so it takes the
  // line when analysis is on; traffic colouring returns when it is off.
  const riskGradient = useMemo(
    () => (risk ? buildRiskGradient(risk.segments, risk.total_distance) : null),
    [risk],
  );
  const gradient = riskGradient || congestionGradient;

  // --- hover the primary line to arm the drag-to-insert ghost -----------------
  useEffect(() => {
    const map = mapRef?.getMap?.();
    if (!map || !interactive || primaryCoords.length < 2) return undefined;

    const offsets = legOffsets[state.primaryIndex] || [];

    const onMove = (e: any) => {
      if (draggingRef.current) return;
      const point: Position = [e.lngLat.lng, e.lngLat.lat];
      const hit = nearestPointOnLine(primaryCoords, point);
      if (!hit) return;
      // Work in screen space so the grab zone feels the same at every zoom.
      const projected = map.project({ lng: hit.point[0], lat: hit.point[1] });
      const pixels = Math.hypot(projected.x - e.point.x, projected.y - e.point.y);
      if (pixels > GHOST_HIT_RADIUS_PX) {
        setGhost(null);
        return;
      }
      const insertion = insertionIndexForPoint(primaryCoords, offsets, hit.point);
      setGhost(insertion ? { position: hit.point, index: insertion.index } : null);
    };

    const onLeave = () => {
      if (!draggingRef.current) setGhost(null);
    };

    map.on('mousemove', onMove);
    map.on('mouseout', onLeave);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onLeave);
    };
  }, [mapRef, interactive, primaryCoords, legOffsets, state.primaryIndex]);

  // --- hover / click alternatives --------------------------------------------
  useEffect(() => {
    const map = mapRef?.getMap?.();
    if (!map || !interactive) return undefined;

    const onEnter = (e: any) => {
      const feature = e.features?.[0];
      if (!feature) return;
      map.getCanvas().style.cursor = 'pointer';
      const index = Number(feature.properties?.routeIndex);
      dispatch({ type: 'HOVER_ROUTE', index });
      setTooltip({ position: [e.lngLat.lng, e.lngLat.lat], index });
    };
    const onMove = (e: any) => {
      if (!tooltip) return;
      setTooltip((cur) => (cur ? { ...cur, position: [e.lngLat.lng, e.lngLat.lat] } : cur));
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = '';
      dispatch({ type: 'HOVER_ROUTE', index: null });
      setTooltip(null);
    };
    const onClick = (e: any) => {
      const feature = e.features?.[0];
      if (!feature) return;
      dispatch({ type: 'SET_PRIMARY', index: Number(feature.properties?.routeIndex) });
      setTooltip(null);
    };

    const attach = () => {
      if (!map.getLayer(LYR_ALT_LINE)) return;
      map.on('mouseenter', LYR_ALT_LINE, onEnter);
      map.on('mousemove', LYR_ALT_LINE, onMove);
      map.on('mouseleave', LYR_ALT_LINE, onLeave);
      map.on('click', LYR_ALT_LINE, onClick);
    };
    attach();
    map.on('styledata', attach);
    return () => {
      map.off('styledata', attach);
      map.off('mouseenter', LYR_ALT_LINE, onEnter);
      map.off('mousemove', LYR_ALT_LINE, onMove);
      map.off('mouseleave', LYR_ALT_LINE, onLeave);
      map.off('click', LYR_ALT_LINE, onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, interactive, dispatch, Boolean(tooltip)]);

  const handleGhostDragEnd = useCallback((event: { lngLat: { lng: number; lat: number } }) => {
    setDraggingGhost(false);
    if (!ghost) return;
    dispatch({
      type: 'INSERT_WAYPOINT',
      index: ghost.index,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
    setGhost(null);
  }, [ghost, dispatch]);

  const badges = useMemo(() => state.routes.map((route, index) => {
    const position = midpointOfLine((route.geometry.coordinates || []) as Position[]);
    if (!position) return null;
    return { index, position, route };
  }).filter(Boolean) as Array<{ index: number; position: Position; route: PlannerState['routes'][number] }>,
  [state.routes]);

  return (
    <>
      <Source id={SRC_FAILED} type="geojson" data={failedData}>
        <Layer
          id={LYR_FAILED}
          type="line"
          paint={{
            'line-color': '#e0563d',
            'line-width': 2,
            'line-dasharray': [2, 2],
            'line-opacity': 0.9,
          }}
        />
      </Source>

      <Source id={SRC_ALTERNATIVES} type="geojson" data={alternativesData}>
        <Layer
          id={LYR_ALT_CASING}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{ 'line-color': '#12181f', 'line-width': 7, 'line-opacity': 0.45 }}
        />
        <Layer
          id={LYR_ALT_LINE}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{
            'line-color': [
              'case',
              ['get', 'alternate'], ALTERNATE_TEAL,
              ['get', 'hovered'], '#c8d8f0',
              MUTED,
            ],
            'line-width': ['case', ['any', ['get', 'hovered'], ['get', 'alternate']], 5, 3.5],
            'line-opacity': [
              'case',
              ['get', 'alternate'], 0.9,
              ['get', 'hovered'], 0.95,
              0.6,
            ],
          }}
        />
      </Source>

      <Source id={SRC_PRIMARY} type="geojson" data={primaryData} lineMetrics>
        <Layer
          id={LYR_PRIMARY_CASING}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{ 'line-color': '#0b1017', 'line-width': 10, 'line-opacity': 0.8 }}
        />
        <Layer
          id={LYR_PRIMARY_LINE}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={
            gradient
              ? { 'line-width': 6, 'line-gradient': gradient as any }
              : { 'line-width': 6, 'line-color': PRIMARY_BLUE }
          }
        />
      </Source>

      {badges.map(({ index, position, route }) => {
        const isPrimary = index === state.primaryIndex;
        const primaryRoute = state.routes[state.primaryIndex];
        return (
          <Marker key={`badge-${index}`} longitude={position[0]} latitude={position[1]} anchor="center">
            <Box
              onClick={() => dispatch({ type: 'SET_PRIMARY', index })}
              onMouseEnter={() => dispatch({ type: 'HOVER_ROUTE', index })}
              onMouseLeave={() => dispatch({ type: 'HOVER_ROUTE', index: null })}
              sx={{
                px: 0.75, py: 0.25, borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
                bgcolor: isPrimary ? PRIMARY_BLUE : 'rgba(18,24,31,0.88)',
                color: '#fff',
                border: '1px solid',
                borderColor: isPrimary ? '#ffffff66' : '#ffffff22',
                fontSize: isPrimary ? '0.7rem' : '0.65rem',
                fontWeight: isPrimary ? 800 : 600,
                boxShadow: isPrimary ? '0 2px 8px rgba(0,0,0,0.45)' : 'none',
              }}
            >
              {formatDuration(route.duration)} · {formatDistance(route.distance, state.options.units)}
              {!isPrimary && primaryRoute && (
                <Box component="span" sx={{ opacity: 0.75, ml: 0.5 }}>
                  {formatDelta(
                    route.duration - primaryRoute.duration,
                    route.distance - primaryRoute.distance,
                    state.options.units,
                  ).split(' · ')[0]}
                </Box>
              )}
            </Box>
          </Marker>
        );
      })}

      {(risk?.hotspots || []).map((segment) => (
        <Marker
          key={`risk-${segment.index}`}
          longitude={segment.midpoint[0]}
          latitude={segment.midpoint[1]}
          anchor="center"
        >
          <Tooltip
            placement="top"
            title={(
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {segment.label || 'Corridor finding'}
                </Typography>
                {segment.findings.map((finding) => (
                  <Typography key={finding} variant="caption" sx={{ display: 'block' }}>
                    {finding}
                  </Typography>
                ))}
              </Box>
            )}
          >
            <Box
              aria-label={`Corridor finding: ${segment.findings.join('. ')}`}
              sx={{
                width: 13,
                height: 13,
                borderRadius: '50%',
                bgcolor: 'transparent',
                border: `3px solid ${riskColor(segment.score)}`,
                boxShadow: '0 0 0 1px rgba(11,16,23,0.85)',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Marker>
      ))}

      {state.waypoints.map((wp, index) => {
        const isOrigin = index === 0;
        const isDestination = index === state.waypoints.length - 1 && state.waypoints.length > 1;
        const letter = String(index + 1);
        const color = isOrigin ? '#2e9e5b' : isDestination ? '#e0563d' : GOLD;
        return (
          <Marker
            key={wp.id}
            longitude={wp.longitude}
            latitude={wp.latitude}
            anchor="center"
            draggable={interactive}
            onDragStart={() => dispatch({ type: 'BEGIN_EDIT' })}
            onDragEnd={(e) => dispatch({
              type: 'MOVE_WAYPOINT',
              id: wp.id,
              longitude: e.lngLat.lng,
              latitude: e.lngLat.lat,
            })}
          >
            <Box
              tabIndex={0}
              aria-label={`Waypoint ${letter}${wp.label ? `: ${wp.label}` : ''}`}
              sx={{
                width: 22, height: 22, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: color, color: '#0b1017',
                fontSize: '0.68rem', fontWeight: 800,
                border: '2px solid rgba(255,255,255,0.9)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                cursor: interactive ? 'grab' : 'default',
                transition: 'transform 120ms ease',
                '&:hover, &:focus-visible': { transform: 'scale(1.15)', outline: 'none' },
              }}
            >
              {letter}
            </Box>
          </Marker>
        );
      })}

      {interactive && ghost && (
        <Marker
          longitude={ghost.position[0]}
          latitude={ghost.position[1]}
          anchor="center"
          draggable
          onDragStart={() => { setDraggingGhost(true); dispatch({ type: 'BEGIN_EDIT' }); }}
          onDragEnd={handleGhostDragEnd}
        >
          <Box
            sx={{
              width: 14, height: 14, borderRadius: '50%',
              bgcolor: '#ffffff', border: `3px solid ${PRIMARY_BLUE}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
              cursor: 'grab',
            }}
          />
        </Marker>
      )}

      {hoverPoint && (
        <Marker longitude={hoverPoint.longitude} latitude={hoverPoint.latitude} anchor="center">
          <Box sx={{
            width: 12, height: 12, borderRadius: '50%',
            bgcolor: GOLD, border: '2px solid rgba(255,255,255,0.95)',
            boxShadow: '0 0 10px rgba(245,185,66,0.9)', pointerEvents: 'none',
          }} />
        </Marker>
      )}

      {tooltip && state.routes[tooltip.index] && state.routes[state.primaryIndex] && (
        <Marker longitude={tooltip.position[0]} latitude={tooltip.position[1]} anchor="bottom" offset={[0, -14]}>
          <Box sx={{
            px: 1, py: 0.5, borderRadius: 1, bgcolor: 'rgba(11,16,23,0.94)',
            border: '1px solid rgba(255,255,255,0.18)', pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, display: 'block' }}>
              {formatDuration(state.routes[tooltip.index].duration)} · {formatDistance(state.routes[tooltip.index].distance, state.options.units)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#c8d8f0' }}>
              {formatDelta(
                state.routes[tooltip.index].duration - state.routes[state.primaryIndex].duration,
                state.routes[tooltip.index].distance - state.routes[state.primaryIndex].distance,
                state.options.units,
              )} vs primary
            </Typography>
          </Box>
        </Marker>
      )}
    </>
  );
};

export default RoutePlannerLayers;
